import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Tag, Package, Loader2, Mail, Phone, Star, ShoppingBag, User,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { MediaGallery } from "../components/MediaGallery";
import { RatingSummary, RatingPanel } from "../components/Rating";
import { SaveButton } from "../components/SaveButton";
import { ShareButton } from "../components/ShareButton";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { toast } from "sonner";

const SALE_TYPE_LABELS = {
  wholesale: "Wholesale",
  retail: "Retail",
  both: "Wholesale & Retail",
};

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get(`/products/${id}`);
      setProduct(res.data);
    } catch {
      toast.error("Product not found");
      navigate("/products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-6 sm:py-10 md:py-14">
      <button
        data-testid="back-to-products"
        onClick={() => navigate("/products")}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6 sm:mb-8"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("tab_products")}
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-12 mb-10 lg:mb-12">
        <div className="lg:col-span-5">
          <div className="relative">
            <div className="absolute top-4 left-4 z-10 flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-[10px] tracking-[0.15em] uppercase font-bold border border-border">
              <Tag className="h-3 w-3" strokeWidth={2.5} />
              {product.category}
            </div>
            <MediaGallery media={product.media} />
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="flex items-center gap-2 mb-4">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-3 py-1 text-[10px] tracking-[0.2em] uppercase font-bold">
              <ShoppingBag className="h-3 w-3" />
              {SALE_TYPE_LABELS[product.sale_type] || product.sale_type}
            </div>
          </div>
          <h1
            data-testid="product-title"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] break-words"
          >
            {product.title}
          </h1>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <SaveButton targetType="product" targetId={product.id} variant="button" stopPropagation={false} />
            <ShareButton
              targetType="product"
              targetId={product.id}
              title={product.title}
              description={product.description}
              variant="button"
              stopPropagation={false}
            />
          </div>
          {product.rating_count > 0 && (
            <div className="mt-4">
              <RatingSummary avg={product.rating_avg || 0} count={product.rating_count || 0} size={5} />
            </div>
          )}
          <p className="mt-6 text-base text-foreground/80 leading-relaxed whitespace-pre-wrap">
            {product.description}
          </p>

          <div className="mt-8 rounded-md bg-foreground text-background p-6 grid grid-cols-2 gap-6">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold opacity-60 mb-1">
                {t("price_per_unit")}
              </div>
              <div className="font-mono-brand text-3xl font-bold">{product.price}</div>
            </div>
            {product.moq && (
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold opacity-60 mb-1">
                  {t("moq_label").replace(" (optional)", "")}
                </div>
                <div className="font-display text-xl font-semibold">{product.moq}</div>
              </div>
            )}
            {product.stock && (
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold opacity-60 mb-1">
                  {t("stock_label").replace(" (optional)", "")}
                </div>
                <div className="font-display text-xl font-semibold">{product.stock}</div>
              </div>
            )}
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold opacity-60 mb-1">
                {t("seller_profile")}
              </div>
              <button
                onClick={() => navigate(`/s/${encodeURIComponent(product.seller_name)}`)}
                data-testid="product-seller-link"
                className="font-display text-xl font-semibold hover:underline text-left"
              >
                {product.seller_name}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="contact" className="w-full">
        <TabsList className="bg-white border border-border rounded-md p-1 h-auto w-full sm:w-auto flex-nowrap overflow-x-auto scrollbar-none justify-start">
          <TabsTrigger data-testid="tab-contact" value="contact" className="rounded-sm data-[state=active]:bg-foreground data-[state=active]:text-background gap-2 px-4 py-2">
            <Mail className="h-3.5 w-3.5" />
            {t("contact_tab")}
          </TabsTrigger>
          <TabsTrigger data-testid="tab-ratings" value="ratings" className="rounded-sm data-[state=active]:bg-foreground data-[state=active]:text-background gap-2 px-4 py-2">
            <Star className="h-3.5 w-3.5" />
            {t("ratings_tab")}
            <span className="font-mono-brand text-[10px] opacity-70">{product.rating_count || 0}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="contact" className="mt-8">
          <div className="max-w-2xl">
            <div className="rounded-md border border-border bg-white p-8 space-y-5" data-testid="contact-panel">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-1">
                    {t("contact_seller")}
                  </div>
                  <button
                    onClick={() => navigate(`/s/${encodeURIComponent(product.seller_name)}`)}
                    className="font-display text-2xl font-bold hover:text-primary transition-colors"
                  >
                    {product.seller_name}
                  </button>
                </div>
                <button
                  onClick={() => navigate(`/s/${encodeURIComponent(product.seller_name)}`)}
                  className="rounded-full border border-border px-4 py-2 text-xs font-semibold hover:border-foreground/30 transition-colors flex items-center gap-1.5"
                  data-testid="view-seller-btn"
                >
                  <User className="h-3.5 w-3.5" />
                  {t("view_seller")}
                </button>
              </div>
              <ContactRow icon={<Mail className="h-4 w-4" />} label={t("email_label")} value={product.email} href={product.email ? `mailto:${product.email}` : null} testId="product-email" />
              <ContactRow icon={<Phone className="h-4 w-4" />} label={t("phone_label")} value={product.phone} href={product.phone ? `tel:${product.phone.replace(/\s+/g, "")}` : null} testId="product-phone" />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="ratings" className="mt-8">
          <div className="max-w-2xl">
            <div className="mb-6">
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-2">
                Rate this product
              </div>
              <RatingSummary avg={product.rating_avg || 0} count={product.rating_count || 0} size={5} />
            </div>
            <RatingPanel targetType="product" targetId={product.id} onRated={load} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ContactRow({ icon, label, value, href, testId }) {
  if (!value) {
    return (
      <div className="flex items-center gap-3 py-2 border-b border-border last:border-0">
        <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center text-muted-foreground">
          {icon}
        </div>
        <div>
          <div className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
            {label}
          </div>
          <div className="text-sm text-muted-foreground">Not provided</div>
        </div>
      </div>
    );
  }
  return (
    <a
      href={href}
      data-testid={testId}
      className="flex items-center gap-3 py-3 border-b border-border last:border-0 hover:bg-secondary/50 -mx-2 px-2 rounded-md transition-colors"
    >
      <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">
          {label}
        </div>
        <div className="font-mono-brand text-sm font-medium truncate">{value}</div>
      </div>
    </a>
  );
}
